import React, { useEffect, useState } from 'react';
import { useAddCalendarMeetingObjectivesStatusMutation } from '../../api/calendar.js';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import BaseButton from '../form/BaseButton.jsx';
import AfterMeetingListItem from './AfterMeetingListItem.jsx';
import AfterMeetingStep3 from './AfterMeetingStep3.jsx';
import BaseCard from './BaseCard.jsx';

const AfterMeetingStep2 = ({ meetingId, calendarMeeting, showOverlay, calendarMeetingUserNotes }) => {
    const [notesState, setNotesState] = useState([]);
    const [showSecondOverlay, setShowSecondOverlay] = useState(false);
    const [addCalendarMeetingObjectivesStatus, { isLoading }] = useAddCalendarMeetingObjectivesStatusMutation();

    useEffect(() => {
        if (showOverlay && Array.isArray(calendarMeetingUserNotes)) {
            const initializedNotes = calendarMeetingUserNotes.map((note) => ({
                ...note,
                marked: note.is_met
            }));
            setNotesState(initializedNotes);
        }
    }, [showOverlay, calendarMeetingUserNotes]);

    const handleMarkNote = (index) => {
        const updatedNotes = [...notesState];
        updatedNotes[index].marked = true;
        setNotesState(updatedNotes);
    };

    const handleUnmarkNote = (index) => {
        const updatedNotes = [...notesState];
        updatedNotes[index].marked = false;
        setNotesState(updatedNotes);
    };

    const renderNotes = () => {
        if (notesState.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No objectives found.</div>;
        }

        return notesState.map(({ id, text, marked }, index) => (
            <AfterMeetingListItem
                key={id}
                text={text}
                isMarked={marked}
                onMark={() => handleMarkNote(index)}
                onUnmark={() => handleUnmarkNote(index)}
                editable
                isLoading={isLoading}
            />
        ));
    };

    const formatMarkedNotes = () => {
        const objectives = notesState.reduce((result, note) => {
            result[note.id] = note.marked;
            return result;
        }, {});
        return { objectives };
    };

    const handleNext = async () => {
        try {
            const formattedNotes = formatMarkedNotes();

            await addCalendarMeetingObjectivesStatus({ id: meetingId, data: formattedNotes });
            setShowSecondOverlay(true);
        } catch (error) {
            useErrorToast('An error occurred while updating objectives.');
        }
    };

    return (
        <>
            <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-8'>
                    <div className='flex items-center gap-[12px] mt-10'>
                        <div className='rounded-full bg-theme1-primary w-[36px] h-[36px] flex items-center justify-center text-white text-xl'>
                            2
                        </div>
                        <div>
                            <h4 className='font-bold text-base'>Step 2</h4>
                            <p className='text-sm text-theme1-title2'>Objectives Achievement</p>
                        </div>
                    </div>
                    <BaseCard className='mt-6 mb-6 p-4 bg-theme1-white'>{renderNotes()}</BaseCard>
                    <BaseButton
                        className='mt-4 mb-5 btn-red-outline'
                        textValue={isLoading ? 'Updating objectives...' : 'Next'}
                        onClick={handleNext}
                        disabled={isLoading}
                    />
                </div>
            </BaseOverlay>
            <MemoizedAfterMeetingStep3
                calendarMeeting={calendarMeeting}
                meetingId={meetingId}
                showSecondOverlay={showSecondOverlay}
            />
        </>
    );
};

const MemoizedAfterMeetingStep3 = React.memo(AfterMeetingStep3);

export default AfterMeetingStep2;
